import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Wallet, IndianRupee, Clock, CheckCircle, ArrowRight } from "lucide-react";
import { Form, Modal } from "react-bootstrap";
import { PageHeader, StatsCard, AppButton, SearchInput, AppBadge, ConfirmModal, EmptyState, Paginator } from "../../ui";
import apiClient from "../../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import "./payouts.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const PAGE_SIZE = 20;

const PayoutsPage = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ totalCollected: 0, totalCommission: 0, totalNetPayable: 0, instituteCount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Settlement modal state
  const [settleTarget, setSettleTarget] = useState(null); // pending payout item
  const [settleForm, setSettleForm] = useState({ method: "bank_transfer", reference: "", notes: "" });
  const [settling, setSettling] = useState(false);
  const [confirmSettle, setConfirmSettle] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get("/sa/payouts/pending", { params: { page, limit: PAGE_SIZE } });
      setPending(data.pendingPayouts || []);
      setGrandTotals(data.grandTotals || { totalCollected: 0, totalCommission: 0, totalNetPayable: 0, instituteCount: 0 });
      setPagination(data.pagination || { page: 1, totalPages: 1, total: (data.pendingPayouts || []).length });
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const filtered = useMemo(() => {
    if (!search) return pending;
    const q = search.toLowerCase();
    return pending.filter(
      (p) =>
        p.institute?.name?.toLowerCase().includes(q) ||
        p.institute?.email?.toLowerCase().includes(q)
    );
  }, [pending, search]);

  // Stats
  const stats = useMemo(() => ({
    totalPending: grandTotals.totalNetPayable || 0,
    totalCommission: grandTotals.totalCommission || 0,
    instituteCount: grandTotals.instituteCount || 0,
  }), [grandTotals]);

  const openSettle = (item) => {
    setSettleTarget(item);
    setSettleForm({ method: "bank_transfer", reference: "", notes: "" });
    setConfirmSettle(false);
  };

  const handleSettle = async () => {
    if (!settleTarget) return;
    try {
      setSettling(true);
      await apiClient.post(`/sa/payouts/settle/${settleTarget.instituteId}`, {
        settlementMethod: settleForm.method,
        settlementReference: settleForm.reference,
        settlementNotes: settleForm.notes,
      });
      setSettleTarget(null);
      setConfirmSettle(false);
      await fetchPending();
    } catch {
      // handle error
    } finally {
      setSettling(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border" /></div>;
  }

  return (
    <div className="sa-payouts">
      <PageHeader
        title="Payouts"
        description="Manage pending settlements to institutes"
        icon={Wallet}
        actions={
          <AppButton variant="outline" size="sm" onClick={() => navigate("/sa/payouts/history")}>
            <Clock size={14} className="me-1" /> Settlement History
          </AppButton>
        }
      />

      {/* Stats */}
      <div className="sa-payouts__stats">
        <StatsCard title="Pending Payout" value={formatCurrency(stats.totalPending)} icon={IndianRupee} iconColor="amber" />
        <StatsCard title="Commission Earned" value={formatCurrency(stats.totalCommission)} icon={Wallet} iconColor="emerald" />
        <StatsCard title="Institutes Pending" value={stats.instituteCount} icon={Clock} iconColor="blue" />
      </div>

      {/* Search */}
      <div className="sa-payouts__toolbar">
        <div className="sa-payouts__search">
          <SearchInput value={search} onChange={setSearch} placeholder="Search institute (current page)…" />
        </div>
      </div>

      {/* Pending Payouts Table */}
      {filtered.length === 0 ? (
        <EmptyState icon={<CheckCircle size={28} />} title="No Pending Payouts" description="All settlements are up to date." />
      ) : (
        <div className="sa-card">
          <div className="sa-card-head">Pending Payouts ({filtered.length})</div>
          <div className="sa-card-body" style={{ overflowX: "auto", padding: 0 }}>
            <table className="payout-table">
              <thead>
                <tr>
                  <th>Institute</th>
                  <th>Collected</th>
                  <th>Commission</th>
                  <th>Net Payable</th>
                  <th>Txns</th>
                  <th>Period</th>
                  <th>Bank Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const bank = item.institute?.bankDetails;
                  const hasBankDetails = bank?.accountNumber && bank?.ifscCode;
                  return (
                    <tr key={item.instituteId}>
                      <td>
                        <div className="payout-table__name">{item.institute?.name || "Unknown"}</div>
                        <div className="payout-table__email">{item.institute?.email}</div>
                      </td>
                      <td className="payout-table__amount">{formatCurrency(item.totalCollected)}</td>
                      <td className="payout-table__amount payout-table__amount--red">{formatCurrency(item.totalCommission)}</td>
                      <td className="payout-table__amount payout-table__amount--green">{formatCurrency(item.netPayable)}</td>
                      <td>{item.transactionCount}</td>
                      <td className="payout-table__period">
                        {formatDate(item.periodStart)} – {formatDate(item.periodEnd)}
                      </td>
                      <td>
                        {hasBankDetails ? (
                          <AppBadge variant="outline" color="emerald">Provided</AppBadge>
                        ) : (
                          <AppBadge variant="outline" color="amber">Missing</AppBadge>
                        )}
                      </td>
                      <td>
                        <AppButton variant="primary" size="sm" onClick={() => openSettle(item)}>
                          Settle <ArrowRight size={14} />
                        </AppButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Paginator
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        pageSize={PAGE_SIZE}
        onChange={setPage}
        infoLabel="institutes"
      />

      {/* Settlement Modal */}
      {settleTarget && !confirmSettle && (
        <Modal show onHide={() => setSettleTarget(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1rem" }}>
              Settle Payout — {settleTarget.institute?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="settle-form">
              <div className="settle-form__summary">
                <div className="settle-form__row">
                  <span className="settle-form__label">Total Collected</span>
                  <span className="settle-form__value">{formatCurrency(settleTarget.totalCollected)}</span>
                </div>
                <div className="settle-form__row">
                  <span className="settle-form__label">Commission</span>
                  <span className="settle-form__value">{formatCurrency(settleTarget.totalCommission)}</span>
                </div>
                <div className="settle-form__row">
                  <span className="settle-form__label">Net Payable</span>
                  <span className="settle-form__value settle-form__value--green">{formatCurrency(settleTarget.netPayable)}</span>
                </div>
                <div className="settle-form__row">
                  <span className="settle-form__label">Transactions</span>
                  <span className="settle-form__value">{settleTarget.transactionCount}</span>
                </div>
              </div>

              {/* Bank details */}
              {settleTarget.institute?.bankDetails?.accountNumber && (
                <div className="bank-details">
                  <div className="bank-details__row">
                    <span>Account Name</span>
                    <span>{settleTarget.institute.bankDetails.accountHolderName || "—"}</span>
                  </div>
                  <div className="bank-details__row">
                    <span>Account No</span>
                    <span>{settleTarget.institute.bankDetails.accountNumber}</span>
                  </div>
                  <div className="bank-details__row">
                    <span>IFSC</span>
                    <span>{settleTarget.institute.bankDetails.ifscCode}</span>
                  </div>
                  <div className="bank-details__row">
                    <span>Bank</span>
                    <span>{settleTarget.institute.bankDetails.bankName || "—"}</span>
                  </div>
                </div>
              )}

              <Form.Group>
                <Form.Label className="sa-settings__label">Settlement Method</Form.Label>
                <Form.Select size="sm" value={settleForm.method} onChange={(e) => setSettleForm((p) => ({ ...p, method: e.target.value }))}>
                  <option value="bank_transfer">Bank Transfer (NEFT/IMPS)</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label className="sa-settings__label">Reference / Transaction ID</Form.Label>
                <Form.Control size="sm" value={settleForm.reference} onChange={(e) => setSettleForm((p) => ({ ...p, reference: e.target.value }))} placeholder="e.g. UTR number" />
              </Form.Group>
              <Form.Group>
                <Form.Label className="sa-settings__label">Notes (optional)</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={settleForm.notes} onChange={(e) => setSettleForm((p) => ({ ...p, notes: e.target.value }))} />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <AppButton variant="outline" size="sm" onClick={() => setSettleTarget(null)}>Cancel</AppButton>
            <AppButton variant="primary" size="sm" onClick={() => setConfirmSettle(true)}>
              <CheckCircle size={14} className="me-1" /> Mark as Settled
            </AppButton>
          </Modal.Footer>
        </Modal>
      )}

      {/* Confirm settlement */}
      <ConfirmModal
        show={confirmSettle}
        onHide={() => setConfirmSettle(false)}
        onConfirm={handleSettle}
        title="Confirm Settlement"
        message={`Are you sure you want to mark ${formatCurrency(settleTarget?.netPayable)} as settled for ${settleTarget?.institute?.name}? This action cannot be undone.`}
        confirmText={settling ? "Settling…" : "Confirm Settlement"}
        variant="success"
        loading={settling}
      />
    </div>
  );
};

export default PayoutsPage;
